const React = require('react');
const T = require('prop-types');
const { NavLink: Link } = require('react-router-dom');
const ChatUtils = require('./utils');
const { default: Classes } = require('./styles.scss');
const Linkify = require('linkifyjs');
const { getOriginalImageUrl } = require('utils/image');
const { getInternalLinkPath, openExternalUrl } = require('utils/internalAppLink');

const { memo, useCallback } = React;

const internals = {};

const areEqual = (prevProps, nextProps) => {

    const prev = React.Children.toArray(prevProps.children).join('|');
    const next = React.Children.toArray(nextProps.children).join('|');

    return prev === next;
};

const ItemBody = function ItemBody(props) {

    const {
        children,
        container,
        messageAttributes,
        isPreview,
        isMe,
        isGroupPreview,
        onOpenMediaViewer,
        ...otherProps
    } = props;

    const renderFullBody = ChatUtils.processFullBody({
        mention: ([name, id]) => {

            // TODO if someone is mentioned twice in the same message, we'll have duplicate keys here.

            // E.g. for @everyone
            if (id === null) {
                return <span key={name} className={Classes.mention}>@{name}</span>;
            }

            return <Link key={name + id + Math.random() * 1000} className={Classes.mention} to={`/app/profile/${id}`}>@{name}</Link>;
        }
    });

    const renderLinks = useCallback((parts) => {

        if (!parts) {
            return null;
        }

        const matchesUrl = (maybeUrl) => {

            return Linkify.test(maybeUrl);
        };

        const matchesEmail = (maybeEmail) => {

            return Linkify.test(maybeEmail, 'email');
        };

        return parts.reduce((out, part) => {

            if (typeof part !== 'string') {
                return out.concat(part);
            }

            const subparts = part.split(/(\s+)/).map((item, index) => { // Put whitespace in group to preserve it

                // If the item is whitespace, return as is
                if (/^\s+$/.test(item)) {
                    return item;
                }

                const clearItem = internals.cleanLinkTarget(item);

                if (!matchesUrl(clearItem) && !matchesEmail(clearItem)) {
                    return item;
                }

                if (matchesEmail(clearItem)) {
                    return <a key={item + index} target='_blank' rel="noopener noreferrer" className={Classes.itemLink} href={`mailto:${clearItem}`}>{item}</a>;
                }

                const internalAppPath = getInternalLinkPath(clearItem);

                if (internalAppPath) {
                    return <Link key={item + index} className={Classes.itemLink} to={internalAppPath}>{item}</Link>;
                }

                return <a key={item + index} target='_blank' rel="noopener noreferrer" className={Classes.itemLink} href={internals.ensureProtocol(clearItem)} onClick={internals.handleExternalLinkClick}>{item}</a>;
            });

            return out.concat(subparts);
        }, []);
    }, []);

    const handleImageClick = useCallback((imageUrl) => {

        if (onOpenMediaViewer) {
            onOpenMediaViewer(imageUrl);
        }
    }, [onOpenMediaViewer]);

    const renderGif = useCallback((message) => {

        try {
            const messageData = typeof message === 'string' ? JSON.parse(message) : message;

            if (messageData.type === 'gif' || messageData.type === 'image') {
                const isNearPeerImage = messageData.url.includes('s3.amazonaws.com/near-peer');
                const isTenorGif = messageData.url.includes('tenor.com');

                if (messageData.type === 'image' && !isNearPeerImage && !isTenorGif) {
                    return messageData.url;
                }

                const imageUrl = messageData.type === 'gif' ? messageData.url : getOriginalImageUrl(messageData.url);

                return (
                    <img
                        src={imageUrl}
                        alt={messageData.type === 'gif' ? 'GIF' : 'Image'}
                        style={{
                            width: '100%',
                            maxWidth: '300px',
                            height: 'auto',
                            minHeight: '150px',
                            borderRadius: '8px',
                            margin: '8px 0',
                            objectFit: 'contain',
                            backgroundColor: 'transparent',
                            cursor: 'pointer'
                        }}
                        onClick={() => handleImageClick(imageUrl)}
                        role="button"
                        tabIndex={0}
                        aria-label="Open image"
                        data-focus-outline="radius:8"
                        onKeyDown={(e) => {

                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleImageClick(imageUrl);
                            }
                        }}
                    />
                );
            }
        }
        catch (e) {

            if (typeof message === 'object' && message.type === 'image' && message.url) {
                return (
                    <img
                        src={message.url}
                        alt="Image"
                        style={{
                            width: '100%',
                            maxWidth: '300px',
                            height: 'auto',
                            minHeight: '150px',
                            borderRadius: '8px',
                            margin: '8px 0',
                            objectFit: 'contain',
                            backgroundColor: 'transparent',
                            cursor: 'pointer'
                        }}
                        onClick={() => handleImageClick(message.url)}
                        role="button"
                        tabIndex={0}
                        aria-label="Open image"
                        data-focus-outline="radius:8"
                        onKeyDown={(e) => {

                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleImageClick(message.url);
                            }
                        }}
                    />
                );
            }
        }

        return null;
    }, [handleImageClick]);

    const renderImage = useCallback((attributes) => {

        if (attributes && attributes.imageUrl) {
            return (
                <img
                    src={attributes.imageUrl}
                    alt="Chat image"
                    style={{
                        width: '100%',
                        maxWidth: '300px',
                        maxHeight: '300px',
                        height: 'auto',
                        minHeight: '150px',
                        borderRadius: '8px',
                        marginTop: '8px',
                        objectFit: 'contain',
                        display: 'block',
                        backgroundColor: 'transparent',
                        cursor: 'pointer'
                    }}
                    onClick={() => handleImageClick(attributes.imageUrl)}
                    role="button"
                    tabIndex={0}
                    aria-label="Open image"
                    data-focus-outline="radius:8"
                    onKeyDown={(e) => {

                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleImageClick(attributes.imageUrl);
                        }
                    }}
                />
            );
        }

        return null;
    }, [handleImageClick]);

    if (isPreview) {
        let previewText = '';

        try {
            const messageData = typeof children === 'string' ? JSON.parse(children) : children;
            if (messageData && messageData.type && messageData.url) {
                if (messageData.type === 'gif') {
                    previewText = isGroupPreview ? 'sent a GIF' : (isMe ? 'You sent a GIF' : 'Sent you a GIF');
                }
                else if (messageData.type === 'image') {
                    previewText = isGroupPreview ? 'sent an image' : (isMe ? 'You sent an image' : 'Sent you an image');
                }
                else {
                    previewText = children;
                }
            }
            else {
                previewText = children;
            }
        }
        catch (e) {
            previewText = children;
        }

        previewText = renderFullBody(previewText, true);

        return container ?
            React.cloneElement(container, otherProps, previewText) :
            React.createElement('span', otherProps, previewText);
    }

    const messageBody = children ? [].concat(children).filter(Boolean).join(' ').replaceAll('https://', ' https://').replaceAll('http://', ' https://') : '';

    const gifContent = renderGif(messageBody);

    let content;
    if (gifContent) {
        content = container ?
            React.cloneElement(container, otherProps, gifContent) :
            React.createElement('span', otherProps, gifContent);
    }
    else {
        const imageContent = renderImage(messageAttributes);

        const textContent = messageBody ? renderLinks(renderFullBody(messageBody, true)) : null;

        let finalContent;
        if (imageContent && !textContent) {
            finalContent = imageContent;
        }
        else if (!imageContent && textContent) {
            finalContent = textContent;
        }
        else if (imageContent && textContent) {
            finalContent = (
                <React.Fragment>
                    {textContent}
                    {imageContent}
                </React.Fragment>
            );
        }
        else {
            finalContent = null;
        }

        content = container ?
            React.cloneElement(
                container,
                otherProps,
                finalContent
            ) :
            React.createElement(
                'span',
                otherProps,
                finalContent
            );
    }

    return content;
};

ItemBody.propTypes = {
    children: T.oneOfType([T.string, T.arrayOf(T.string)]),
    container: T.element,
    messageAttributes: T.object,
    isPreview: T.bool,
    isMe: T.bool,
    isGroupPreview: T.bool,
    onOpenMediaViewer: T.func
};

module.exports = memo(ItemBody, areEqual);

internals.cleanLinkTarget = (item) => {

    return item.replace(/^[,]+|[,.;:!?\)'"“”]+$/g, '');
};

internals.ensureProtocol = (url) => {

    if (url.match(/^https?:\/\//i)) {
        return url;
    }

    return `http://${url}`;
};

internals.handleExternalLinkClick = (event) => {

    if (!window.cordova?.InAppBrowser || event.defaultPrevented || event.button !== 0 ||
        event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
    }

    event.preventDefault();
    openExternalUrl(event.currentTarget.href);
};
